import { Ijob, IjobPopulated, Iuser } from '../models';
import { jobStatusEnum, jobTypesIdEnum } from '../models-enums';

/*
 * Review-flow detection helpers.
 *
 * MMG-4165 moved approver assignment to job creation, so the presence of
 * approverID (or matching the approver's identity) is NO LONGER a proxy for
 * "the job is in the review phase". Several FE spots had relied on that timing
 * coincidence and started mis-firing during WIP once the backend changed.
 *
 * These helpers centralise the correct detection so the rest of the app has a
 * single definition and can't drift back into inferring workflow phase from
 * assignment timing. Scoping is made explicit here (job type + status +
 * identity) rather than relying on the backend implicitly guaranteeing that
 * only Inspection jobs ever get an approverID / reach ReviewPending.
 *
 * `jobTypeID` is a required, always-defined field on Ijob (defaults to a
 * non-Inspection type on the stub job), so these checks are safe to call even
 * before a real job is loaded.
 */

/**
 * The job is actively being worked, and therefore allows work order parts and
 * damage codes to be used/edited. The original rule only checked `New`; once
 * `InProgress` was introduced, jobs being actively worked were incorrectly treated
 * like closed/reopened jobs. A reopened, completed, or review-pending job is NOT editable.
 */
export const isJobEditable = (job?: Ijob | null): boolean =>
    job?.status === jobStatusEnum.new || job?.status === jobStatusEnum.inProgress;

/** Only Inspection jobs go through the FSE approval/review flow. */
export const isReviewFlowJob = (job?: Ijob | null): boolean =>
    job?.jobTypeID === jobTypesIdEnum.inspection;

/** The job is currently awaiting approver review (ReviewPending status). */
export const isJobInReviewPhase = (job?: Ijob | null): boolean =>
    job?.status === jobStatusEnum.reviewPending;

/** The given user is the assigned approver for the job. */
export const isJobApprover = (job?: Ijob | null, user?: Iuser | null): boolean =>
    Boolean(job?.approverID) && Boolean(user?.id) && job?.approverID === user?.id;

/** An Inspection job that is currently awaiting approver review. */
export const isJobUnderReview = (job?: Ijob | null): boolean =>
    isReviewFlowJob(job) && isJobInReviewPhase(job);

/**
 * The approver is being asked to review/sign right now: an Inspection job in
 * the review phase whose approver is the current user. This is the predicate
 * that should gate the approver signing screen and approver-only review
 * actions.
 */
export const isApproverReviewing = (job?: Ijob | null, user?: Iuser | null): boolean =>
    isJobUnderReview(job) && isJobApprover(job, user);

/** Approver of a Completed job they did not work on: view only - mirrors Job.IsReadOnlyFor (MMG-4480). */
export const isJobReadOnlyForUser = (job?: Ijob | null, user?: Iuser | null): boolean =>
    job?.status === jobStatusEnum.completed &&
    isJobApprover(job, user) &&
    job?.assignedUserID !== user?.id &&
    !((job as IjobPopulated | null | undefined)?.userJobs ?? []).some(
        userJob => userJob.userID === user?.id && userJob.isDeleted === false
    );
